import { defineStore } from 'pinia';
import { STORE_KEY } from '@/config/storeKey';
import { useLocalStorage } from '@vueuse/core';
import type { GameItem, GameSingleCfg } from '@/components/SelectGame/type';
import { flattenDeep, isPlainObject, groupBy, isString, isArray, isFunction } from 'lodash-es';
import { computed, ref } from 'vue';

export function getGameIcon(item: Pick<GameSingleCfg, 'icon' | 'code'> | string) {
  const { icon, code } = isString(item) ? { icon: item, code: undefined } : item;
  return icon || `https://static.aix.intlgame.cn/imarketing/gameicon/${code || 'default'}.png`;
}
const cfgToDrownOptions = (list: GameSingleCfg[]) =>
  list.map((item) => ({
    value: item.code,
    connect: item.name,
    icon: getGameIcon(item),
    category: item.category || item.code,
  }));

function setValue(target: { value: unknown }, value: unknown) {
  const tempValue = isArray(value) ? value.at(-1) : value;
  // eslint-disable-next-line no-param-reassign
  tempValue && target.value !== tempValue && (target.value = tempValue);
}

enum GameCodeState {
  loading,
  done,
}

export function getGameCodeStorageKeyByLoginType() {
  return `MI_GAME_CODE`;
}

export const useGlobalGameStore = defineStore(STORE_KEY.GLOBAL.GAME, () => {
  const gameCode = ref<string>('');
  const gameCfg = ref<Record<string, GameSingleCfg> | null>(null);
  const gameList = computed<GameItem[]>(() => (gameCfg.value ? gameCfgToGameList(gameCfg.value) : []));
  const currentGameItem = ref<GameItem>({} as unknown as GameItem);
  const gameCodeState = ref<GameCodeState>(GameCodeState.loading);
  const storageGameCode = useLocalStorage<string>(getGameCodeStorageKeyByLoginType(), '');
  const beforeChangeGameHookMap = ref<Record<string, Function>>({});

  const setGameCodeDone = () => {
    gameCodeState.value = GameCodeState.done;
  };
  const setGameCodeLoading = () => {
    gameCodeState.value = GameCodeState.loading;
  };

  const setBeforeChangeGameHook = (module: string, hook: Function) => {
    beforeChangeGameHookMap.value[module] = hook;
  };
  const destoryBeforeChangeGameHook = (module: string) => {
    delete beforeChangeGameHookMap.value[module];
  };

  const isGameCodeDone = computed(() => gameCodeState.value === GameCodeState.done);

  const setGameCode = (newGameCode: string) => {
    // 遍历所有的hook执行
    Object.values(beforeChangeGameHookMap.value).forEach((hook) => {
      if (isFunction(hook)) {
        hook({ oldGame: gameCode.value, newGame: newGameCode });
      }
    });

    setValue(gameCode, newGameCode);
    setValue(storageGameCode, newGameCode);
    if (gameList?.value?.length ?? 0 > 0) {
      setValue(
        currentGameItem,
        flattenDeep(gameList.value!.map((item) => item.children || item)).find((item) => item.value === newGameCode),
      );
    }
  };

  const setGameCfg = (cfg: Record<string, GameSingleCfg>) => {
    if (isPlainObject(cfg) && Object.keys(cfg).length > 0) {
      gameCfg.value = cfg;
    }
  };
  const gameCfgToGameList = (obj: Record<string, GameSingleCfg>): GameItem[] => {
    const list: GameSingleCfg[] = [];
    Object.keys(obj).forEach((key) => {
      list.push(obj[key]);
    });
    const groupByGameCfg = groupBy(cfgToDrownOptions(list), 'category');
    const tempGameList: GameItem[] = [];
    Object.keys(groupByGameCfg).forEach((key) => {
      const item: any = {
        ...groupByGameCfg[key][0],
      };
      if (groupByGameCfg[key].length > 1) {
        item.code = item.category;
        item.connect = item.category;
        item.children = groupByGameCfg[key].map((i) => ({
          ...i,
          parent: item,
          icon: item.icon,
        }));
      }
      tempGameList.push(item);
    });
    return tempGameList;
  };

  return {
    gameCfg,
    gameCode,
    gameList,
    currentGameItem,
    storageGameCode,
    setGameCode,
    setGameCfg,

    setGameCodeDone,
    setGameCodeLoading,
    isGameCodeDone,
    setBeforeChangeGameHook,
    destoryBeforeChangeGameHook,
  };
});
